import React, { useEffect, useState } from "react";
import { Plus, Pencil, Layers, Trash2, Route } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { RoutingDialog } from "./CustomerGroupRouting";

const BUCKETS = ["HOME", "STORE", "PRE_ORDER"];

export default function CustomerGroups() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [routing, setRouting] = useState(null);
  const write = can("warehouse_admin", "sales");
  const canRoute = can("supervisor"); // owner + supervisor

  const load = () => api.get("/customer-groups").then((r) => setList(r.data));
  const remove = async (g) => {
    if (!window.confirm(`Hapus group "${g.name}"?`)) return;
    try { await api.delete(`/customer-groups/${g.id}`); toast.success("Group dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    load();
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => setWarehouses([]));
  }, []);

  return (
    <div data-testid="customer-groups-page">
      <PageHeader title="Customer Group" subtitle="Grouping customer + routing gudang HOME/STORE per bucket (Keep & Sales)">
        {write && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-group-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Group</Button>}
      </PageHeader>

      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Nama Group</TableHead><TableHead>Default Bucket</TableHead><TableHead>Bucket Diizinkan</TableHead>
              <TableHead>Routing</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((g) => (
              <TableRow key={g.id} data-testid={`group-row-${g.id}`}>
                <TableCell className="font-semibold text-cocoa">{g.name}</TableCell>
                <TableCell><Badge variant="outline">{g.default_sales_bucket || "HOME"}</Badge></TableCell>
                <TableCell className="text-xs text-wmuted">{(g.allowed_buckets || []).join(", ") || "-"}</TableCell>
                <TableCell>
                  {g.routing_status === "needs_setup"
                    ? <span className="text-xs text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid={`group-needs-setup-${g.id}`}>Perlu Pengaturan</span>
                    : <span className="text-xs text-status-passed font-medium">Terkonfigurasi</span>}
                </TableCell>
                <TableCell><span className={g.active ? "text-status-passed" : "text-wmuted"}>{g.active ? "Aktif" : "Nonaktif"}</span></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setRouting(g)} className="border-cocoa/30 text-cocoa h-8 mr-1" data-testid={`group-routing-${g.id}`}><Route className="w-3.5 h-3.5 mr-1" />Routing</Button>
                  {write && <Button size="icon" variant="ghost" onClick={() => { setEdit(g); setOpen(true); }} data-testid={`edit-group-${g.id}`}><Pencil className="w-4 h-4 text-cocoa" /></Button>}
                  {write && <Button size="icon" variant="ghost" onClick={() => remove(g)} data-testid={`delete-group-${g.id}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-10"><Layers className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada group.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      <GroupDialog open={open} setOpen={setOpen} edit={edit} warehouses={warehouses} onDone={load} />
      {routing && <RoutingDialog gid={routing.id} groupName={routing.name} canEdit={canRoute} onClose={() => { setRouting(null); load(); }} />}
    </div>
  );
}

const NO_WH = "__default__";

function GroupDialog({ open, setOpen, edit, warehouses, onDone }) {
  const empty = { name: "", note: "", default_warehouse_id: "", active: true, default_sales_bucket: "HOME", allowed_buckets: ["HOME", "STORE", "PRE_ORDER"], allow_split_fulfillment: false, allow_manual_override: false };
  const [form, setForm] = useState(empty);
  useEffect(() => { if (open) setForm(edit ? { ...empty, ...edit, default_warehouse_id: edit.default_warehouse_id || "" } : empty); }, [open, edit]);

  const toggleBucket = (b) => setForm((f) => {
    const has = f.allowed_buckets.includes(b);
    const next = has ? f.allowed_buckets.filter((x) => x !== b) : [...f.allowed_buckets, b];
    const patch = { allowed_buckets: next };
    if (!next.includes(f.default_sales_bucket)) patch.default_sales_bucket = next[0] || "HOME";
    return { ...f, ...patch };
  });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama group wajib diisi");
    if (!form.allowed_buckets.length) return toast.error("Pilih minimal satu bucket yang diizinkan");
    const payload = {
      name: form.name, note: form.note, default_warehouse_id: form.default_warehouse_id || null, active: form.active,
      default_sales_bucket: form.default_sales_bucket, allowed_buckets: form.allowed_buckets,
      allow_split_fulfillment: !!form.allow_split_fulfillment, allow_manual_override: !!form.allow_manual_override,
    };
    try {
      if (edit) await api.put(`/customer-groups/${edit.id}`, payload);
      else await api.post("/customer-groups", payload);
      toast.success("Group disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? "Edit" : "Tambah"} Group — Pengaturan Umum</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name-input" /></div>

          <div>
            <Label>Bucket yang Diizinkan</Label>
            <div className="flex flex-wrap gap-4 mt-1.5">
              {BUCKETS.map((b) => (
                <label key={b} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`group-allow-${b}`}>
                  <Checkbox checked={form.allowed_buckets.includes(b)} onCheckedChange={() => toggleBucket(b)} />
                  {b}
                </label>
              ))}
            </div>
            <p className="text-xs text-wmuted mt-1">HOME, STORE, PRE_ORDER selalu terlihat. Bucket yang tidak diizinkan tampil disabled di routing.</p>
          </div>

          <div>
            <Label>Default Sales Bucket</Label>
            <Select value={form.default_sales_bucket} onValueChange={(v) => setForm({ ...form, default_sales_bucket: v })}>
              <SelectTrigger data-testid="group-default-bucket"><SelectValue /></SelectTrigger>
              <SelectContent>{form.allowed_buckets.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2"><Switch checked={form.allow_split_fulfillment} onCheckedChange={(v) => setForm({ ...form, allow_split_fulfillment: v })} data-testid="group-split-switch" /><Label className="cursor-pointer">Allow Split Fulfillment</Label></div>
          <div className="flex items-center gap-2"><Switch checked={form.allow_manual_override} onCheckedChange={(v) => setForm({ ...form, allow_manual_override: v })} data-testid="group-override-switch" /><Label className="cursor-pointer">Allow Manual Warehouse Override</Label></div>

          <div>
            <Label>Gudang Default Keep (legacy)</Label>
            <Select value={form.default_warehouse_id || NO_WH} onValueChange={(v) => setForm({ ...form, default_warehouse_id: v === NO_WH ? "" : v })}>
              <SelectTrigger data-testid="group-warehouse-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_WH}>Tidak ada</SelectItem>
                {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-wmuted mt-1">Field lama; dipetakan otomatis ke routing HOME prioritas 1 saat migrasi. Atur routing detail di tombol "Routing".</p>
          </div>

          <div><Label>Catatan</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="group-note-input" /></div>
          <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="group-active-switch" /><Label>Aktif</Label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-group-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
